import { useCallback, useLayoutEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';
import {
  captureRowAnchor,
  parsePosition,
  restoreRowAnchor,
  RowAnchor,
  serializePosition,
} from 'lib/rowAnchor';

/**
 * Another market picked: the match on screen stays where it is and shows the
 * new odds there, instead of the list moving under the player. Wrap the
 * change: `keep(() => setMarket(key), header)`. The component calling this
 * must re-render with the change (its layout effect puts the row back).
 */
export const useKeepRowInPlace = () => {
  const pending = useRef<{ anchor: RowAnchor; at: number } | null>(null);
  useLayoutEffect(() => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    // Only right after the change, not on some later render.
    if (Date.now() - p.at > 1000) return;
    restoreRowAnchor(p.anchor);
    // Rows the browser skipped painting (content-visibility) get their real
    // height in the next frame: once more then (not cancelled by the next
    // live update's render).
    requestAnimationFrame(() => restoreRowAnchor(p.anchor));
  });
  return useCallback((change: () => void, under?: HTMLElement | null) => {
    const line = under
      ? Math.max(0, under.getBoundingClientRect().bottom)
      : undefined;
    const anchor = captureRowAnchor(line);
    pending.current = anchor ? { anchor, at: Date.now() } : null;
    change();
  }, []);
};

const storageKey = (key: string) => `list-pos:${key}`;

/** How long the way back waits for the list's rows to come. */
const RESTORE_WAIT_MS = 5000;
/** ...and how long the row must have stood in its place. */
const SETTLE_MS = 800;

/**
 * A list's place, kept while the player looks around it: going back to it
 * (from a match, the slip) or reloading the page brings the same match to the
 * same place on the screen, once its rows are drawn again. Opening the list
 * anew from the menu starts at its top, as before.
 */
export const useListPosition = (key: string | null) => {
  // How the player came to the list (or to this list of the page).
  const how = useNavigationType();
  const navigation = useRef(how);
  navigation.current = how;

  useLayoutEffect(() => {
    if (!key) return;
    const id = storageKey(key);
    let restoring = false;
    let timer: number | undefined;
    let frame = 0;
    let stop = () => {};

    const save = () => {
      if (restoring) return;
      const anchor = captureRowAnchor();
      // Nothing listed yet (loading): what was kept stays.
      if (!anchor) return;
      try {
        sessionStorage.setItem(id, serializePosition(anchor, Date.now()));
      } catch {
        // A convenience only.
      }
    };
    const onScroll = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(save, 150);
    };

    // Back to the list (or a reload): the same match where it stood.
    if (navigation.current === 'POP') {
      let saved: RowAnchor | null = null;
      try {
        saved = parsePosition(sessionStorage.getItem(id), Date.now());
      } catch {
        saved = null;
      }
      if (saved) {
        const anchor = saved;
        restoring = true;
        const started = Date.now();
        // The player scrolls first: his scroll wins.
        stop = () => {
          restoring = false;
          cancelAnimationFrame(frame);
          window.removeEventListener('touchstart', stop);
          window.removeEventListener('wheel', stop);
          window.removeEventListener('keydown', stop);
        };
        window.addEventListener('touchstart', stop, { passive: true });
        window.addEventListener('wheel', stop, { passive: true });
        window.addEventListener('keydown', stop);
        let placedSince = 0;
        const tick = () => {
          if (!restoring) return;
          // The row may come late (a list asked again), and the page may be
          // too short for it at first (rows drawn a batch per frame).
          const r = restoreRowAnchor(anchor);
          const now = Date.now();
          if (r !== 'placed') placedSince = 0;
          else if (!placedSince) placedSince = now;
          // In place for a moment, while the rows and the parts above them
          // settle (a list asked again, the VIP hero).
          if (
            (placedSince && now - placedSince > SETTLE_MS) ||
            now - started > RESTORE_WAIT_MS
          ) {
            stop();
            return;
          }
          frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // Before a tap opens a match or the slip, while the list is on screen.
    document.addEventListener('pointerdown', save, true);
    window.addEventListener('pagehide', save);
    return () => {
      stop();
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('pointerdown', save, true);
      window.removeEventListener('pagehide', save);
    };
  }, [key]);
};
